import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import {
  ContentListProps,
  DescriptionProps,
} from "@companion-core/shared/app/Components/DetailsPages/EditorialDetailsPage/EditorialDetailsPage.model";
import { APIVendorComponent } from "@companion-core/shared/app/Interfaces/API";
import { ComponentCard } from "@companion-core/shared/app/Components/Cards/ComponentCard";
import { EditorialTemplateProps } from "./editorialTemplate.model";
import { useEditorialTemplate } from "../hooks/useEditorialTemplate";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { useWebNavigationHeader } from "@companion-core/web/src/Hooks/navigationHeader";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { useParams } from "react-router";

export const Description = ({ description, isMobileSize }: DescriptionProps) => {
  const { t } = useTranslation();
  const isLongDescription = useMemo(() => description.length > 260, [description]);
  const [allVisible, setAllVisible] = useState<boolean>(
    !(isMobileSize && isLongDescription) ? true : false
  );
  const shortDescription = useMemo(() => {
    const limit = description.indexOf(" ", 260);
    return limit > -1 ? description.slice(0, limit) : description;
  }, [description]);
  const htmlDescription = useMemo(
    () =>
      isMobileSize && isLongDescription && !allVisible
        ? shortDescription.replace(/\n/g, "<br>")
        : description.replace(/\n/g, "<br>"),
    [description, shortDescription, isMobileSize, isLongDescription, allVisible]
  );
  const toggleVisibility = () => {
    setAllVisible(!allVisible);
  };
  return (
    <section
      data-testid="editorial-details-page__description"
      className="editorial-details-page__description">
      <div
        data-testid="editorial-details-page__description-text"
        className={`editorial-details-page__description-text body-text-6 ${!allVisible ? "editorial-details-page__description-text--short" : ""}`}
        // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlDescription) }}
      />
      {isMobileSize && isLongDescription && (
        <span
          data-testid="editorial-details-page__description-toggle"
          className="editorial-details-page__description-toggle body-text-7"
          onClick={toggleVisibility}>
          {allVisible
            ? t("dynamic-content.editorial.details.description.see-less")
            : t("dynamic-content.editorial.details.description.see-more")}
          <FontIcon name={allVisible ? "JP-Arrow-up-bold" : "JP-Arrow-down-bold"} />
        </span>
      )}
    </section>
  );
};

export const ContentList = ({ contentList, pageType }: ContentListProps) => {
  const category = useMemo(() => contentList.category, [contentList]);
  return (
    <section
      data-testid={`editorial-details-page__content-list--${category}`}
      className="editorial-details-page__content-list">
      <h3
        data-testid={`editorial-details-page__content-title--${category}`}
        className="editorial-details-page__content-title title-5">
        {category}
      </h3>
      <div
        data-testid={`editorial-details-page__content-items--${category}`}
        className={`editorial-details-page__content-items ${contentList.items?.length > 1 ? "editorial-details-page__content-items--multiple" : ""}`}>
        {contentList.items?.map((item: APIVendorComponent, index: number) => (
          <ComponentCard
            key={item.id}
            component={item}
            pageSlug={pageType}
            nbItems={contentList.items.length}
            position={index}
            url={window.location.href}
            variant="vendor"
            fixedHeight={true}
          />
        ))}
      </div>
    </section>
  );
};

export const EditorialTemplate = ({ data }: EditorialTemplateProps) => {
  const { origin } = useParams();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const {
    t,
    bannerImageUrl,
    editorialContentLists,
    editorialDescription,
    editorialSubtitle,
    editorialTitle,
  } = useEditorialTemplate({ data, pageSlug: origin ?? "", isMobileSize });
  const { navigateBackward } = useWebNavigationHeader();

  return (
    <div className="editorial-details-page" data-testid="editorial-details-page">
      {!isMobileSize && (
        <div data-testid="editorial-details-page__back-button" onClick={() => navigateBackward()}>
          <FontIcon
            name="JP-Arrow-left-bold"
            size={24}
            className="editorial-details-page__back-button"
          />
        </div>
      )}

      {bannerImageUrl && (
        <Banner
          title={editorialTitle}
          subtitle={editorialSubtitle}
          imageUrl={bannerImageUrl}
          overlap={false}
          boxShadow
        />
      )}

      <div className="editorial-details-page__layout">
        {editorialDescription && (
          <Description description={editorialDescription} isMobileSize={isMobileSize} />
        )}

        {editorialContentLists.length && (
          <div className="editorial-details-page__title-container">
            <h2
              className={`editorial-details-page__title ${!isMobileSize ? "title-1" : "title-7"}`}>
              {t("dynamic-content.editorial.details.addresses.title")}
            </h2>
            <hr className="editorial-details-page__title-divider" />
          </div>
        )}

        <div className={"editorial-details-page__lists"}>
          {editorialContentLists.map((editorialContentList) => (
            <ContentList
              key={editorialContentList.category}
              contentList={editorialContentList}
              pageType={origin ?? ""}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
